import {
  Avatar,
  CircularProgress,
  Grid,
  Hidden,
  InputAdornment,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { PopoverProps } from '@material-ui/core/Popover';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import ConfirmationDialog from '../../../../../components/ConfirmationDialog';
import CustomDialog from '../../../../../components/CustomDialog';
import SelectPeriod from '../../../../../components/SelectPeriod';
import { useLoadingDialog } from '../../../../../hooks/backdrop';
import { useFeedback } from '../../../../../hooks/feedback';
import { EmployeeDTO } from '../../../../../models/dto/EmployeeDTO';
import { PeriodDTO } from '../../../../../models/dto/PeriodDTO';
import {
  findObservations,
  saveObservation,
} from '../../../../../services/personnelDepartment.service';
import { formatCurrency } from '../../../../../utils/formatCurrency';

interface GratificationsDTO {
  value: number;
  label: string;
}

interface EmployeePunchCardObservationsDialog extends PopoverProps {
  employeeData: EmployeeDTO;
  period: PeriodDTO;
  handleClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fieldLabelText: {
      fontWeight: 'bold',
    },
    selectButton: {
      textAlign: 'center',
    },
    selectMenu: {
      padding: 0,
      '&&:focus': {
        backgroundColor: 'transparent',
      },
    },
    menuPaper: {
      maxHeight: 350,
    },
  }),
);

const gratificationsOptions: GratificationsDTO[] = [
  {
    value: 1,
    label: 'Gratificações',
  },
  {
    value: 2,
    label: 'Acúmulo de Função',
  },
  {
    value: 3,
    label: 'Salário Substituto',
  },
];

const EmployeePunchCardObservationsDialog: React.FC<EmployeePunchCardObservationsDialog> = ({
  employeeData,
  period,
  open,
  handleClose,
}) => {
  const classes = useStyles();
  const { addFeedback } = useFeedback();
  const { handleLoadingDialog } = useLoadingDialog();
  const [disableFields, setDisableFields] = useState(false);
  const [loadingObservations, setLoadingObservations] = useState(false);
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO>(period);
  const [
    gratificationSelected,
    setGratificationSelected,
  ] = useState<GratificationsDTO | null>(null);
  const [gratifiedTime, setGratifiedTime] = useState<string>('');
  const [bonusAmount, setBonusAmount] = useState<string>('0.00');
  const [note, setNote] = useState<string>('');
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const clearForm = () => {
    setGratificationSelected(null);
    setGratifiedTime('');
    setNote('');
    setBonusAmount('0.00');
  };

  const getObservation = async (
    employeeCode: number,
    periodRequest: PeriodDTO,
  ) => {
    setLoadingObservations(true);
    try {
      const { data } = await findObservations({
        employeeCode,
        period: periodRequest,
      });

      if (data.length > 0) {
        setDisableFields(true);
        setGratifiedTime(data[0].quantidadeMinutos.toString());
        setNote(data[0].observacao);
        setBonusAmount(data[0].valorBonificacao.toFixed(2).toString());
        setGratificationSelected(
          gratificationsOptions.find(
            item => item.label === data[0].gratificacao,
          ) || null,
        );
      } else {
        setDisableFields(false);
        clearForm();
      }
    } catch (error) {
      console.log(error);
      setDisableFields(true);
      clearForm();
    } finally {
      setLoadingObservations(false);
    }
  };

  const handleSubmit = async () => {
    setOpenConfirmationDialog(false);
    handleLoadingDialog(true);
    try {
      if (gratificationSelected) {
        await saveObservation({
          dataPeriodoApuracao: moment(
            periodSelected?.mesFechamento,
            'DD/MM/YYYY',
          ).format('YYYY-MM-DD'),
          observacao: note,
          numeroCadastro: employeeData.numeroCadastro,
          valorBonificacao: Number(bonusAmount),
          gratificacao: gratificationSelected?.label,
          quantidadeMinutos: Number(gratifiedTime),
        });
      }
      addFeedback({
        description: 'Observação adicionada com sucesso',
        type: 'success',
      });
    } catch (error) {
      console.log(error);
      addFeedback({
        description: 'Ocorreu um erro ao realizar a operação',
        type: 'error',
      });
    } finally {
      handleLoadingDialog(false);
      await getObservation(employeeData.numeroCadastro, periodSelected);
    }
  };

  useEffect(() => {
    setPeriodSelected(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getObservation(employeeData.numeroCadastro, periodSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodSelected]);

  return (
    <>
      <CustomDialog
        open={open}
        handleClose={handleClose}
        handleConfirm={() => setOpenConfirmationDialog(true)}
        maxWidth="xs"
        fullWidth={false}
        contentDividers
        keepMounted={false}
        showCloseButton
        title="Adicionar Observação"
        disabledOkButton={
          gratifiedTime.length === 0 ||
          !gratificationSelected ||
          loadingObservations ||
          disableFields
        }
        okButtonLabel="Salvar"
      >
        <Grid
          container
          style={{ marginBottom: 24 }}
          justify="center"
          alignItems="center"
          spacing={1}
          direction="row"
        >
          <Hidden smDown>
            <Grid item md={2}>
              <Avatar />
            </Grid>
          </Hidden>
          <Grid item xs={12} md={10}>
            <Typography
              color="textPrimary"
              style={{
                fontWeight: 'bolder',
                marginRight: 16,
                display: 'inline',
              }}
              variant="subtitle1"
            >
              {employeeData?.nome}
            </Typography>
            {loadingObservations && (
              <CircularProgress size={20} color="primary" />
            )}
            <Typography color="textSecondary" variant="body2">
              {employeeData?.cargo?.descricao}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography
              className={classes.fieldLabelText}
              color="textPrimary"
              variant="body1"
            >
              Período:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <SelectPeriod
              disableUnderline
              value={periodSelected}
              handlePeriodChange={setPeriodSelected}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography
              className={classes.fieldLabelText}
              color="textPrimary"
              variant="body1"
            >
              Gratificações:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Select
              MenuProps={{ classes: { paper: classes.menuPaper } }}
              classes={{
                root: classes.selectButton,
                selectMenu: classes.selectMenu,
              }}
              placeholder="Selecionar"
              disabled={disableFields || loadingObservations}
              disableUnderline
              value={gratificationSelected?.value || 0}
            >
              <MenuItem disabled value={0} style={{ padding: 0 }}>
                <ListItem component="nav">
                  <ListItemText primary="Selecionar" />
                </ListItem>
              </MenuItem>
              {gratificationsOptions.map((gratification, index) => (
                <MenuItem
                  key={index}
                  value={gratification.value}
                  selected={
                    gratification.value === gratificationSelected?.value
                  }
                  style={{ padding: 0 }}
                >
                  <ListItem
                    component="nav"
                    onClick={() => setGratificationSelected(gratification)}
                  >
                    <ListItemText primary={gratification?.label} />
                  </ListItem>
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography
              className={classes.fieldLabelText}
              color="textPrimary"
              variant="body1"
            >
              Qtd. Minutos:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              variant="outlined"
              disabled={disableFields || loadingObservations}
              style={{ marginLeft: 16 }}
              type="number"
              size="small"
              value={gratifiedTime}
              onChange={event =>
                setGratifiedTime(event.target.value.replace(/[^0-9]/g, ''))
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography
              className={classes.fieldLabelText}
              color="textPrimary"
              variant="body1"
            >
              Valor Bonificação:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <OutlinedInput
              type="number"
              disabled={disableFields || loadingObservations}
              style={{ marginLeft: 16 }}
              value={bonusAmount}
              onChange={event =>
                setBonusAmount(formatCurrency(event.target.value))
              }
              startAdornment={
                <InputAdornment position="start">R$</InputAdornment>
              }
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              style={{ marginTop: 8 }}
              placeholder="Observação"
              disabled={disableFields || loadingObservations}
              value={note}
              onChange={event => setNote(event.target.value)}
            />
          </Grid>
        </Grid>
      </CustomDialog>
      <ConfirmationDialog
        open={openConfirmationDialog}
        title="Atenção!"
        mainText="Após concluir esta ação não poderá ser revertida. Deseja continuar?"
        onConfirm={handleSubmit}
        onCancel={() => setOpenConfirmationDialog(false)}
        handleClose={() => setOpenConfirmationDialog(false)}
      />
    </>
  );
};

export default EmployeePunchCardObservationsDialog;
